import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  Plus,
  Pencil,
  Copy,
  Trash2,
  Send,
  Clock,
  CalendarDays,
  Search,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Layers,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IWorkout, IWorkoutAssignment } from '../../../models/programming';
import { Paginated } from '../../../typings/Paginated';
import { WORKOUT_TYPE, ASSIGNMENT_TYPE } from '../../../enums/programming';
import { BLOCK_TYPE_COLORS, BLOCK_TYPE_LABELS } from '../../../enums/programming';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { CustomBadge } from '../../../shared/CustomBadge/CustomBadge';
import WorkoutBuilderModal from '../WorkoutBuilderModal/WorkoutBuilderModal';
import AssignWorkoutModal from '../AssignWorkoutModal/AssignWorkoutModal';
import styles from './WorkoutsTab.module.scss';

const WORKOUT_TYPE_LABELS: Record<WORKOUT_TYPE, string> = {
  [WORKOUT_TYPE.SINGLE_DAY]: 'Giorno singolo',
  [WORKOUT_TYPE.MULTI_DAY]: 'Multi-giorno',
};

const WorkoutsTab = () => {
  const queryClient = useQueryClient();
  const { getProgrammingWorkouts, duplicateProgrammingWorkout, deleteProgrammingWorkout, getProgrammingAssignments } = useEndpoints();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState<IWorkout | null>(null);
  const [assigningWorkout, setAssigningWorkout] = useState<IWorkout | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: playlistAssignments } = useQuery({
    queryKey: ['programming-assignments', 'playlist-all'],
    queryFn: async () => {
      const { data } = await getProgrammingAssignments({ limit: 200 });
      return (
        (data as Paginated<IWorkoutAssignment> | undefined)?.docs ??
        (data as unknown as IWorkoutAssignment[] | undefined) ??
        []
      ).filter((a: IWorkoutAssignment) => a.assignmentType === ASSIGNMENT_TYPE.PLAYLIST);
    },
    staleTime: 5 * 60_000,
  });

  const playlistWorkoutIds = useMemo(
    () =>
      new Set(
        (playlistAssignments ?? [])
          .map((a: any) => {
            if (!a.workoutId) return null;
            return typeof a.workoutId === 'object' ? a.workoutId._id : a.workoutId;
          })
          .filter((id: string | null) => id != null),
      ),
    [playlistAssignments],
  );

  const workoutsQuery = useQuery({
    queryKey: ['programming-workouts', debouncedSearch, typeFilter, page],
    queryFn: async () => {
      const { data } = await getProgrammingWorkouts({
        search: debouncedSearch || undefined,
        type: typeFilter || undefined,
        isTemplate: true,
        page,
        limit: 20,
      });
      return data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => duplicateProgrammingWorkout(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programming-workouts'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProgrammingWorkout(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programming-workouts'] }),
  });

  const workouts = useMemo(() => workoutsQuery.data?.docs ?? [], [workoutsQuery.data]);
  const totalPages = workoutsQuery.data?.totalPages ?? 1;
  const totalDocs = workoutsQuery.data?.totalDocs ?? 0;

  const handleEdit = useCallback((workout: IWorkout) => {
    setEditingWorkout(workout);
    setBuilderOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setEditingWorkout(null);
    setBuilderOpen(true);
  }, []);

  const handleBuilderClose = useCallback(() => {
    setBuilderOpen(false);
    setEditingWorkout(null);
  }, []);

  // Count total blocks across all days
  const getTotalBlocks = (workout: IWorkout) =>
    workout.days.reduce((sum, day) => sum + (day.blocks?.length ?? 0), 0);

  return (
    <div className={styles.container}>
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterPills}>
          <button
            className={`${styles.filterPill} ${typeFilter === '' ? styles.filterPillActive : ''}`}
            onClick={() => { setTypeFilter(''); setPage(1); }}
          >
            Tutte
            {totalDocs > 0 && (
              <span className={styles.pillCount}>{totalDocs}</span>
            )}
          </button>
          <button
            className={`${styles.filterPill} ${typeFilter === WORKOUT_TYPE.SINGLE_DAY ? styles.filterPillActive : ''}`}
            onClick={() => { setTypeFilter(WORKOUT_TYPE.SINGLE_DAY); setPage(1); }}
          >
            Giorno singolo
          </button>
          <button
            className={`${styles.filterPill} ${typeFilter === WORKOUT_TYPE.MULTI_DAY ? styles.filterPillActive : ''}`}
            onClick={() => { setTypeFilter(WORKOUT_TYPE.MULTI_DAY); setPage(1); }}
          >
            Multi-giorno
          </button>
        </div>
        <button className={styles.addBtn} onClick={handleNew}>
          <Plus size={15} strokeWidth={2.5} />
          Nuova Scheda
        </button>
      </div>

      {/* Search */}
      <div className={styles.searchWrapper}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Cerca scheda per nome..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className={styles.searchInput}
        />
      </div>

      {/* Content */}
      {workoutsQuery.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : workouts.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrap}>
            <ClipboardList size={24} strokeWidth={1.5} />
          </div>
          <p className={styles.emptyTitle}>Nessuna scheda trovata</p>
          <p className={styles.emptySubtitle}>
            {search || typeFilter
              ? 'Prova a modificare i filtri di ricerca'
              : 'Crea la tua prima scheda per iniziare'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {workouts.map((workout) => {
              const totalBlocks = getTotalBlocks(workout);

              return (
                <div key={workout._id} className={styles.card}>
                  <div className={styles.cardBody}>
                    {/* Header */}
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitleArea}>
                        <p className={styles.workoutTitle}>{workout.title}</p>
                        <div className={styles.cardBadges}>
                          <span className={styles.typeBadge}>
                            {workout.type === WORKOUT_TYPE.MULTI_DAY
                              ? <Layers size={11} strokeWidth={2.5} />
                              : <CalendarDays size={11} strokeWidth={2.5} />
                            }
                            {WORKOUT_TYPE_LABELS[workout.type] || workout.type}
                          </span>
                          {workout.isLeopartTemplate && (
                            <span className={styles.leopartBadge}>Leopart</span>
                          )}
                          {playlistWorkoutIds.has(workout._id) && (
                            <CustomBadge
                              label="Playlist"
                              color="light-blue-dust"
                            />
                          )}
                        </div>
                      </div>
                      <div className={styles.cardActions}>
                        {!workout.isLeopartTemplate && (
                          <button
                            className={styles.actionIconBtn}
                            onClick={() => handleEdit(workout)}
                            title="Modifica"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        <button
                          className={styles.actionIconBtn}
                          onClick={() => duplicateMutation.mutate(workout._id)}
                          title="Duplica"
                        >
                          <Copy size={14} />
                        </button>
                        {!workout.isLeopartTemplate && (
                          <button
                            className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
                            onClick={() => {
                              if (window.confirm('Eliminare questa scheda?')) {
                                deleteMutation.mutate(workout._id);
                              }
                            }}
                            title="Elimina"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Day preview */}
                    {workout.days.length > 0 && (
                      <div className={styles.dayPreview}>
                        {workout.days.slice(0, 3).map((day) => (
                          <div key={day.dayNumber} className={styles.dayItem}>
                            <span className={styles.dayLabel}>G{day.dayNumber}</span>
                            <span className={styles.dayTitle}>
                              {day.title || `Giorno ${day.dayNumber}`}
                            </span>
                            <span className={styles.dayBlockCount}>
                              {day.blocks?.length ?? 0}
                            </span>
                            {/* Block type dots */}
                            {day.blocks && day.blocks.length > 0 && (
                              <div className={styles.dayBlockDots}>
                                {day.blocks.slice(0, 4).map((block, i) => (
                                  <span
                                    key={i}
                                    className={styles.blockDot}
                                    style={{ background: BLOCK_TYPE_COLORS[block.type] || '#757575' }}
                                    title={BLOCK_TYPE_LABELS[block.type] || block.type}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {workout.days.length > 3 && (
                          <span className={styles.moreDays}>
                            +{workout.days.length - 3} altr{workout.days.length - 3 === 1 ? 'o' : 'i'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Tags */}
                    {workout.tags.length > 0 && (
                      <div className={styles.cardTags}>
                        {workout.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className={styles.tag}>{tag}</span>
                        ))}
                        {workout.tags.length > 3 && (
                          <span className={styles.tag}>+{workout.tags.length - 3}</span>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className={styles.cardFooter}>
                      <div className={styles.cardMeta}>
                        <span className={styles.metaItem}>
                          <CalendarDays size={13} strokeWidth={2.5} />
                          {workout.days.length} giorn{workout.days.length === 1 ? 'o' : 'i'}
                        </span>
                        {totalBlocks > 0 && (
                          <span className={styles.metaItem}>
                            <Layers size={13} strokeWidth={2.5} />
                            {totalBlocks} blocc{totalBlocks === 1 ? 'o' : 'hi'}
                          </span>
                        )}
                        {workout.estimatedDuration && (
                          <span className={styles.metaItem}>
                            <Clock size={13} strokeWidth={2.5} />
                            ~{workout.estimatedDuration} min
                          </span>
                        )}
                      </div>
                      <button
                        className={styles.assignBtn}
                        onClick={() => setAssigningWorkout(workout)}
                      >
                        <Send size={12} strokeWidth={2.5} />
                        Assegna
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <span className={styles.paginationInfo}>
                Pagina {page} di {totalPages}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageCounter}>{page} / {totalPages}</span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <WorkoutBuilderModal
        isOpen={builderOpen}
        onClose={handleBuilderClose}
        workout={editingWorkout}
      />

      <AssignWorkoutModal
        isOpen={!!assigningWorkout}
        onClose={() => setAssigningWorkout(null)}
        workout={assigningWorkout}
      />
    </div>
  );
};

export default WorkoutsTab;
